import { Dispatch, SetStateAction, useEffect } from 'react';

import { ConditionalRender } from '@crossnokaye/ux-common';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crossnokaye/ux-common/ckds';

import {
  ActionComponentStateConfig,
  ConfirmationDialogConfig,
  DropdownOption,
  Schedule,
  Sequence,
} from '@repo/sdk/models/components';

import { getRefValue } from '@/features/equipment-browser/utils';
import { DropdownButton } from '@/ux-common/components/dropdown-button/dropdown-button';

import { ActionIcon } from './action-icon';

type SettingValueKind = string | number | boolean | Schedule | Sequence | undefined;

export const ConfirmationDescription = ({
  data,
  setData,
  state,
  requiresConfirmation,
}: {
  requiresConfirmation: ConfirmationDialogConfig;
  data: Record<string, SettingValueKind>;
  setData: Dispatch<SetStateAction<Record<string, SettingValueKind>>>;
  state: ActionComponentStateConfig;
}) => {
  const dropdownConfig = requiresConfirmation.dropdownComponentConfig;

  useEffect(() => {
    if (dropdownConfig) {
      const currentRefValue = dropdownConfig?.valueRef
        ? getRefValue(dropdownConfig.valueRef)
        : undefined;
      setData((prev) => ({ ...prev, [dropdownConfig.id as string]: currentRefValue }));
    }
    return () => {
      setData({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownConfig]);

  const selectedOption = dropdownConfig?.options.find(
    (option: DropdownOption) => option.value === data[dropdownConfig?.id as string]
  );

  return (
    <ConditionalRender condition={!!dropdownConfig}>
      <DropdownMenu>
        <DropdownMenuTrigger
          data-state-name={state?.stateName}
          asChild
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownButton className="btn btn-default" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              {selectedOption && (
                <ActionIcon
                  icon={selectedOption.icon?.icon ?? ''}
                  variant={selectedOption.icon?.variant}
                />
              )}
              <span className="text-ckds-neutral-900">
                {selectedOption?.label ?? 'Select Mode'}
              </span>
            </div>
          </DropdownButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="min-w-72"
          onClick={(e) => e.stopPropagation()}
        >
          {dropdownConfig?.subtitle && (
            <>
              <DropdownMenuLabel>{dropdownConfig?.subtitle}</DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          )}
          {dropdownConfig?.options.map((option: DropdownOption) => {
            const actionIcon = option.icon;

            const DropdownMenuItemComponent = (
              <DropdownMenuItem
                disabled={dropdownConfig?.disabled ?? false}
                key={option.label}
                onClick={(e) => {
                  e.stopPropagation();
                  setData((prev) => ({ ...prev, [dropdownConfig.id as string]: option.value }));
                }}
              >
                {actionIcon && <ActionIcon icon={actionIcon.icon} variant={actionIcon.variant} />}
                <span className="text-ckds-neutral-900">{option.label}</span>
              </DropdownMenuItem>
            );

            if (dropdownConfig?.disabled ?? false) {
              return <div>{DropdownMenuItemComponent}</div>;
            }
            return DropdownMenuItemComponent;
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </ConditionalRender>
  );
};
